import { useEffect, useMemo, useState } from 'react';

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export default function useTypewriter({ words, typeMs = 55, deleteMs = 32, pauseMs = 900 } = {}) {
  const safeWords = useMemo(() => (Array.isArray(words) && words.length ? words : ['']), [words]);
  const [index, setIndex] = useState(0);
  const [text, setText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setText(String(safeWords[0] || ''));
      return;
    }

    const current = String(safeWords[index % safeWords.length] || '');

    // pause when fully typed
    if (!isDeleting && text === current) {
      const t = setTimeout(() => setIsDeleting(true), pauseMs);
      return () => clearTimeout(t);
    }

    // move to next word when fully deleted
    if (isDeleting && text === '') {
      setIsDeleting(false);
      setIndex((i) => (i + 1) % safeWords.length);
      return;
    }

    const next = isDeleting
      ? current.substring(0, Math.max(0, text.length - 1))
      : current.substring(0, Math.min(current.length, text.length + 1));

    const speed = isDeleting ? deleteMs : typeMs;
    const t = setTimeout(() => setText(next), speed);
    return () => clearTimeout(t);
  }, [safeWords, index, text, isDeleting, typeMs, deleteMs, pauseMs]);

  return { text, isReducedMotion: prefersReducedMotion() };
}
